// List selector — replaces a persistent left sidebar with a single
// header chip that shows the current list and opens a dropdown of
// all lists + "New list" row.

import { useEffect, useRef, useState } from "preact/hooks";
import { Icon } from "../../icons";
import type { ShoppingList } from "../../lib/rpc";
import { confirm } from "../../components/confirm";

interface ListSelectorProps {
  lists: ShoppingList[];
  activeListId: number | null;
  setActiveListId: (id: number) => void;
  openCount: number;
  totalCount: number;
  onCreateList: (name: string) => void;
  onDeleteList: (listId: number) => void;
}

export function ListSelector({
  lists, activeListId, setActiveListId, openCount, totalCount, onCreateList, onDeleteList,
}: ListSelectorProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const active = lists.find((l) => l.id === activeListId) || null;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const createList = () => {
    const name = prompt("New list name?");
    if (name && name.trim()) {
      onCreateList(name.trim());
      setOpen(false);
    }
  };

  // Group the dropdown into Personal (private to me) and Household
  // (shared with my household) so it's always clear which scope a list
  // lives in — and the same name can appear in both without confusion.
  const renderSection = (label: string, sectionLists: ShoppingList[]) => {
    if (sectionLists.length === 0) return null;
    return (
      <>
        <div className="list-selector-section-label">{label}</div>
        {sectionLists.map((l) => (
          <div
            key={l.id}
            className="list-selector-option"
            data-active={l.id === activeListId}
            onClick={() => { setActiveListId(l.id); setOpen(false); }}
          >
            <span className="list-selector-emoji">🛒</span>
            <span className="list-selector-option-title">{l.name}</span>
            <button
              className="list-selector-delete"
              title={`Delete ${l.name}`}
              onClick={async (e) => {
                e.stopPropagation();
                const ok = await confirm({
                  title: `Delete "${l.name}"?`,
                  message: "All items in this list will be removed. This can't be undone.",
                  confirmLabel: "Delete list",
                  danger: true,
                });
                if (!ok) return;
                onDeleteList(l.id);
                setOpen(false);
              }}
              type="button"
            >
              <Icon name="trash" size={12} />
            </button>
          </div>
        ))}
      </>
    );
  };

  return (
    <div className="list-selector" data-open={open} ref={ref}>
      <button
        className="list-selector-btn"
        onClick={() => setOpen((v) => !v)}
        type="button"
      >
        <span className="list-selector-emoji">🛒</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="list-selector-title">
            {active?.name || "No list"}
          </div>
          <div className="list-selector-sub">
            {active?.sharedWithHousehold ? "household" : "personal"} · {totalCount} item{totalCount === 1 ? "" : "s"}
          </div>
        </div>
        <span className="list-selector-count">{openCount} open</span>
        <span className="list-selector-chev" aria-hidden>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m6 9 6 6 6-6" />
          </svg>
        </span>
      </button>

      {open && (
        <div className="list-selector-menu" role="menu">
          {renderSection("Personal", lists.filter((l) => !l.sharedWithHousehold))}
          {renderSection("Household", lists.filter((l) => !!l.sharedWithHousehold))}
          <div className="list-selector-divider" />
          <button className="list-selector-newrow" onClick={createList} type="button">
            <Icon name="plus" size={14} />
            New list
          </button>
        </div>
      )}
    </div>
  );
}
